export type OptionObjProps = Record<string, string[]>;

export const Options: OptionObjProps = {
  quickstart_path: [
    "Notecard and Notecarrier A",
    "Notecard and Notecarrier F",
    "Notecard and Notecarrier Pi",
    "Notecard and Notecarrier X",
    "Notecard Simulator",
    "Notecard and SparkFun Qwiic Cellular",
    "Notecard and SparkFun MicroMod Cellular Function Board",
  ],
  notecarrier: [
    "Notecarrier A",
    "Notecarrier AA",
    "Notecarrier AE",
    "Notecarrier AL",
    "Notecarrier AF",

    // The sensor tutorial requires an unversioned listing
    "Notecarrier F",
    "Notecarrier Pi",

    // Versioned listings for the datasheets
    "Notecarrier F v1.0",
    "Notecarrier F v1.3",
    "Notecarrier B v1.0",
    "Notecarrier B v2.1",
    "Notecarrier B v3.1",
    "Notecarrier Pi v1.0",
    "Notecarrier Pi v2.0",
    "Notecarrier R v1.0",
    "Notecarrier X v1.2",
    "Notecarrier XM v1.2",
    "Notecarrier XP v3.1",
    "Notecarrier XS v1.1",
    "Notecarrier XS v1.2",
    "SparkFun MicroMod Cellular Function Board",
    "Sparkfun Qwiic Cellular",
  ],
  notecard: [
    "NOTE-WBGLW",
    "NOTE-WBGLWT",
    "NOTE-NBGLW",
    "NOTE-NBGL-500",
    "NOTE-WBNAW",
    "NOTE-NBNAW",
    "NOTE-WBNA-500",
    "NOTE-NBNA-500",
    "NOTE-WBEXW",
    "NOTE-WBEX-500",
    "NOTE-WIFI",
    "NOTE-ESP",
    "NOTE-LORA v1.4",
    "NOTE-LORA v2.1",
    "NOTE-NBNAN",
    "NOTE-NBGLN",
    "NOTE-WBNAN",
    "NOTE-WBEXN",
    "NOTE-MBNAN",
    "NOTE-MBNAW",
    "NOTE-MBGLN",
    "NOTE-MBGLW",
  ],
  board: [
    "Arduino Nano 33 BLE Sense",
    "Artemis Thing Plus",
    "Adafruit HUZZAH32",
    "Adafruit Feather M4 Express",
    "Blues Swan",
    "Raspberry Pi",
    "SparkFun MicroMod STM32 Processor",
    "STM32 Discovery",
    "STM32 Nucleo",
  ],
  language: [
    "CircuitPython",
    "C/C++ (Arduino/Wiring)",
    "C/C++ (STM32Cube)",
    "Python",
    "JavaScript",
    "STMDuino",
  ],
  service: [
    "ThingSpeak",
    "Azure IoT Central",
    "General HTTP/HTTPS",
    "Snowflake",
    "Initial State",
    "IoT Central",
    "InfluxDB",
    "AWS IoT Analytics",
    "Ubidots",
    "Google Cloud Platform",
    "Datacake",
    "MQTT",
    "ThingWorx",
    "Blynk",
  ],
  route_schema: [
    "AWS",
    "Azure",
    "Blynk",
    "Datacake",
    "HTTPS",
    "Google Cloud",
    "MQTT",
    "Proxy for Notecard Web Requests",
    "RadNote RadResponder",
    "Slack",
    "Snowflake",
    "ThingWorx",
    "Twilio",
  ],
  swan_datasheet_version: ["Swan v1.5", "Swan v1.7", "Swan v3.0"],
  cygnet_datasheet_version: ["Cygnet v1.2"],
  airnote_datasheet_version: ["Airnote v1.0", "Airnote v2.0"],
  notecard_firmware_version: [
    "9.x",
    "8.x LTS",
    "7.x",
    "6.x LTS",
    "5.x",
    "4.x LTS",
    "3.x",
    "2.x LTS",
  ],
};

export const getCategorizedOptions = (
  key: string,
  options: string[],
): { [key: string]: string[] } => {
  switch (key) {
    case "notecard":
      return {
        "Notecard Cellular": options.filter((o) => o.endsWith("n")).sort(),
        "Notecard Cellular (Legacy)": options
          .filter((o) => o.endsWith("-500"))
          .sort(),
        "Notecard Cell+WiFi": options
          .filter((o) => o.endsWith("w") || o.endsWith("wt"))
          .sort(),
        "Notecard LoRa": options
          .filter((o) => o.startsWith("note-lora"))
          .sort(),
        "Notecard WiFi": ["note-esp", "note-wifi"],
      };
    case "notecarrier":
      return {
        "Notecarrier A": ["notecarrier-a"],
        "Notecarrier A (Legacy)": [
          "notecarrier-aa",
          "notecarrier-ae",
          "notecarrier-af",
          "notecarrier-al",
        ],
        "Notecarrier B": options
          .filter((o) => o.includes("notecarrier-b"))
          .sort(),
        "Notecarrier F": options
          .filter((o) => o.includes("notecarrier-f"))
          .sort(),
        "Notecarrier Pi": options
          .filter((o) => o.includes("notecarrier-pi"))
          .sort(),
        "Notecarrier R": options
          .filter((o) => o.includes("notecarrier-r"))
          .sort(),
        "Notecarrier X": options
          .filter((o) => o.includes("notecarrier-x-"))
          .sort(),
        "Notecarrier XM": options
          .filter((o) => o.includes("notecarrier-xm"))
          .sort(),
        "Notecarrier XP": options
          .filter((o) => o.includes("notecarrier-xp"))
          .sort(),
        "Notecarrier XS": options
          .filter((o) => o.includes("notecarrier-xs"))
          .sort(),
      };
    default:
      throw new Error(`Unsupported categorization option: ${key}`);
  }
};

export const getProductNameBySKU = (sku: string) => {
  switch (sku) {
    case "NOTE-WBGLW":
    case "NOTE-WBGLWT":
      return "Notecard Cell+WiFi | LTE Cat-1 | Global";
    case "NOTE-NBGLW":
      return "Notecard Cell+WiFi | Cat-M | Global";
    case "NOTE-WBNAW":
      return "Notecard Cell+WiFi | LTE Cat-1 | North America";
    case "NOTE-NBNAW":
      return "Notecard Cell+WiFi | Cat-M | North America";
    case "NOTE-WBEXW":
      return "Notecard Cell+WiFi | LTE Cat-1 | EMEA";
    case "NOTE-WIFI":
    case "NOTE-ESP":
      return "Notecard WiFi";
    case "NOTE-LORA-V1-4":
    case "NOTE-LORA-V2-1":
      return "Notecard for LoRa";
    case "NOTE-NBNA-500":
    case "NOTE-NBNAN":
      return "Notecard Cellular | Cat-M | North America";
    case "NOTE-NBGL-500":
    case "NOTE-NBGLN":
      return "Notecard Cellular | Cat-M | Global";
    case "NOTE-WBNA-500":
    case "NOTE-WBNAN":
      return "Notecard Cellular | LTE Cat-1 | North America";
    case "NOTE-WBEX-500":
    case "NOTE-WBEXN":
      return "Notecard Cellular | LTE Cat-1 | EMEA";
    case "NOTE-MBNAN":
      return "Notecard Cellular | LTE Cat-1 bis | North America";
    case "NOTE-MBNAW":
      return "Notecard Cell+WiFi | LTE Cat-1 bis | North America";
    case "NOTE-MBGLN":
      return "Notecard Cellular | LTE Cat-1 bis | Global";
    case "NOTE-MBGLW":
      return "Notecard Cell+WiFi | LTE Cat-1 bis | Global";
    default:
      throw new Error(`Unknown Notecard SKU: ${sku}`);
  }
};

export const getProductDescriptionBySKU = (sku: string) => {
  let ratDescription = "";

  switch (sku) {
    // Notecard Cell+WiFi
    case "NOTE-WBGLW":
    case "NOTE-WBGLWT":
    case "NOTE-NBGLW":
    case "NOTE-WBNAW":
    case "NOTE-NBNAW":
    case "NOTE-WBEXW":
    case "NOTE-MBNAW":
    case "NOTE-MBGLW":
      ratDescription = "cellular and Wi-Fi";
      break;
    // Notecard WiFi
    case "NOTE-WIFI":
    case "NOTE-ESP":
      ratDescription = "Wi-Fi";
      break;
    // Notecard for LoRa
    case "NOTE-LORA-V1-4":
    case "NOTE-LORA-V2-1":
      ratDescription = "LoRa/LoRaWAN";
      break;
    // Notecard Cellular
    case "NOTE-NBNA-500":
    case "NOTE-NBNAN":
    case "NOTE-NBGL-500":
    case "NOTE-NBGLN":
    case "NOTE-WBNA-500":
    case "NOTE-WBNAN":
    case "NOTE-WBEX-500":
    case "NOTE-WBEXN":
    case "NOTE-MBNAN":
    case "NOTE-MBGLN":
      ratDescription = "cellular";
      break;
    default:
      throw new Error(`Unknown Notecard SKU: ${sku}`);
  }

  return `Device-to-cloud data pump that reduces the complexity of building connected solutions with ${ratDescription}.`;
};

export const getProductImageBySKU = (sku: string) => {
  switch (sku) {
    case "NOTE-WBGLW":
    case "NOTE-WBGLWT":
      return "/images/hardware/notecard/WBGLW-Front.png";
    case "NOTE-NBGLW":
      return "/images/hardware/notecard/NBGLW-Front.png";
    case "NOTE-WBNAW":
      return "/images/hardware/notecard/WBNAW-Front.png";
    case "NOTE-NBNAW":
      return "/images/hardware/notecard/NBNAW-Front.png";
    case "NOTE-WBEXW":
      return "/images/hardware/notecard/WBEXW-Front.png";
    case "NOTE-WIFI":
      return "/images/hardware/notecard/note_wifi.png";
    case "NOTE-ESP":
      return "/images/hardware/notecard/ESP-Front.png";
    case "NOTE-LORA-V1-4":
      return "/images/hardware/notecard/LORA-Front.png";
    case "NOTE-LORA-V2-1":
    case "NOTE-LWUS":
    case "NOTE-LWEU":
      return "/images/hardware/notecard/LORA-21-Front.png";
    case "NOTE-NBNA-500":
    case "NOTE-NBGL-500":
    case "NOTE-WBNA-500":
    case "NOTE-WBEX-500":
      return "/images/hardware/notecard/Legacy-Front.png";
    case "NOTE-NBNAN":
      return "/images/hardware/notecard/NBNAN-Front.png";
    case "NOTE-NBGLN":
      return "/images/hardware/notecard/NBGLN-Front.png";
    case "NOTE-WBNAN":
      return "/images/hardware/notecard/WBNAN-Front.png";
    case "NOTE-WBEXN":
      return "/images/hardware/notecard/WBEXN-Front.png";
    case "NOTE-MBNAN":
      return "/images/hardware/notecard/MBNAN-Front.png";
    case "NOTE-MBNAW":
      return "/images/hardware/notecard/MBNAW-Front.png";
    case "NOTE-MBGLN":
      return "/images/hardware/notecard/MBGLN-Front.png";
    case "NOTE-MBGLW":
      return "/images/hardware/notecard/MBGLW-Front.png";
    default:
      // eslint-disable-next-line no-console
      console.error(`Unknown Notecard SKU: ${sku}`);
      return "";
  }
};

export const getProductShopUrlBySKU = (sku: string) => {
  switch (sku) {
    // Notecard Cell+WiFi
    case "NOTE-WBGLW":
    case "NOTE-WBGLWT":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=43940463182065";
    case "NOTE-NBGLW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=44564760002801";
    case "NOTE-WBNAW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=43953568710897";
    case "NOTE-NBNAW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=44564759970033";
    case "NOTE-WBEXW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=43953568743665";
    case "NOTE-MBNAW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=44596849737969";
    case "NOTE-MBGLW":
      return "https://shop.blues.com/products/notecard-cell-wifi?variant=44596849770737";
    // Notecard WiFi
    case "NOTE-WIFI":
    case "NOTE-ESP":
      return "https://shop.blues.com/products/wifi-notecard";
    // Notecard for LoRa
    case "NOTE-LORA-V1-4":
    case "NOTE-LORA-V2-1":
      return "https://shop.blues.com/products/notecard-lora";
    // Notecard Cellular
    case "NOTE-NBNA-500":
      return "https://shop.blues.com/products/notecard-cellular?variant=43930998964465";
    case "NOTE-NBNAN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541263151345";
    case "NOTE-NBGL-500":
      return "https://shop.blues.com/products/notecard-cellular?variant=43930999030001";
    case "NOTE-NBGLN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541263184113";
    case "NOTE-WBNA-500":
      return "https://shop.blues.com/products/notecard-cellular?variant=43930998997233";
    case "NOTE-WBNAN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541263085809";
    case "NOTE-WBEX-500":
      return "https://shop.blues.com/products/notecard-cellular?variant=43940458856689";
    case "NOTE-WBEXN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541263118577";
    case "NOTE-MBNAN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541416997105";
    case "NOTE-MBGLN":
      return "https://shop.blues.com/products/notecard-cellular?variant=44541417029873";
    default:
      throw new Error(`Unknown Notecard SKU: ${sku}`);
  }
};

export const getPriceBySKU = (sku: string) => {
  switch (sku) {
    // Notecard Cell+WiFi
    case "NOTE-WBGLW":
    case "NOTE-WBGLWT":
      return "72.00";
    case "NOTE-NBGLW":
      return "59.00";
    case "NOTE-WBNAW":
      return "57.00";
    case "NOTE-NBNAW":
      return "49.00";
    case "NOTE-WBEXW":
      return "67.00";
    case "NOTE-MBNAW":
      return "53.00";
    case "NOTE-MBGLW":
      return "63.00";
    // Notecard WiFi
    case "NOTE-WIFI":
    case "NOTE-ESP":
      return "10.00";
    // Notecard for LoRa
    case "NOTE-LORA-V1-4":
    case "NOTE-LORA-V2-1":
      return "10.00";
    // Notecard Cellular
    case "NOTE-NBNA-500":
      return "49.00";
    case "NOTE-NBNAN":
      return "45.00";
    case "NOTE-NBGL-500":
      return "59.00";
    case "NOTE-NBGLN":
      return "55.00";
    case "NOTE-WBNA-500":
      return "59.00";
    case "NOTE-WBNAN":
      return "53.00";
    case "NOTE-WBEX-500":
      return "69.00";
    case "NOTE-WBEXN":
      return "63.00";
    case "NOTE-MBNAN":
      return "49.00";
    case "NOTE-MBGLN":
      return "59.00";
    default:
      throw new Error(`Unknown Notecard SKU: ${sku}`);
  }
};

// TODO: pass this down in [[...slugs]].tsx when it gets refactored
//       and cleanup DocConfig in config/nav.ts
export type DocMeta = {
  allPaths: string[];
  defaultVals: string[];
  description: string;
  keywords: string[];
  navTitle: string;
  props: string[];
  publicationStatus: string;
  searchOnlyDefaultSlugs?: boolean;
};
